"use client";

import { useState, useEffect } from "react";
import {
  getContributorRoles, getCreatorRoles, getInstruments,
  getCopyrightSocieties, getGenreConfig, getGenresFromWorks, getPublishers,
  DEFAULT_CONTRIBUTOR_ROLES, DEFAULT_CREATOR_ROLES,
  DEFAULT_INSTRUMENTS, DEFAULT_COPYRIGHT_SOCIETIES, DEFAULT_GENRES, DEFAULT_PUBLISHERS,
  type GenreConfigItem,
} from "@/services/app-config";

function makeHook<T>(fetcher: () => Promise<T[]>, fallback: T[]) {
  return function useConfigList(): { items: T[]; loading: boolean } {
    const [items, setItems] = useState<T[]>(fallback);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
      let cancelled = false;
      fetcher().then(data => { if (!cancelled) { setItems(data); setLoading(false); } });
      return () => { cancelled = true; };
    }, []);
    return { items, loading };
  };
}

export const useContributorRoles = makeHook(getContributorRoles, DEFAULT_CONTRIBUTOR_ROLES);
export const useCreatorRoles     = makeHook(getCreatorRoles,     DEFAULT_CREATOR_ROLES);
export const useInstruments      = makeHook(getInstruments,      DEFAULT_INSTRUMENTS);
export const useCopyrightSocieties = makeHook(getCopyrightSocieties, DEFAULT_COPYRIGHT_SOCIETIES);
export const useGenreConfig      = makeHook<GenreConfigItem>(getGenreConfig, DEFAULT_GENRES);
/** Alias for useGenreConfig — every input on BB now reads from the single
 *  canonical `genres` Firestore collection. The old getGenresFromWorks
 *  scanner is preserved as an admin-only migration helper, not a render
 *  source, so we never see drift between artist / track / release / user
 *  autocompletes again. */
export const useMediaGenres      = useGenreConfig;
// (getGenresFromWorks intentionally left exported — used by the admin
//  "back-fill genres from works" migration tool only.)
void getGenresFromWorks;
export const usePublishers       = makeHook(getPublishers, DEFAULT_PUBLISHERS);
